/**
 * IconForge — turns the game's own 3D models into UI icons.
 *
 * ## Why not image files
 *
 * Because there are none, anywhere in this project, and that is load-bearing: the
 * whole game is 500 kB gzipped precisely because every texture, mesh and sound is
 * code. Adding twenty-three weapon portraits as PNGs would be the single largest
 * download in the build, and they would be *worse* than what is proposed here — a
 * drawing of a gun that has to be kept in sync with the gun by hand.
 *
 * So the icon is the weapon. `buildWeaponModel` already produces a full 5–9k
 * triangle model of every entry on the roster; this renders one, once, to a data
 * URL and caches it. The picture cannot drift from the thing it depicts because it
 * *is* the thing, and fitting a suppressor could later change the portrait for free.
 *
 * ## How it shares the renderer
 *
 * One WebGL context, not two. A second `WebGLRenderer` for thumbnails would double
 * the driver-side cost on exactly the devices that can least afford it, so this
 * borrows the live renderer, swaps to a private render target, and puts it back.
 * Every piece of state it touches — target, clear colour, tone mapping — is saved
 * and restored, because leaving any of it changed would show up as a wrong-looking
 * *game*, one frame later, with no obvious cause.
 *
 * ## Lazily, and never during a frame
 *
 * Generating twenty-three models at boot would add real time to a load that is
 * currently under a second. Icons are made on first request — which in practice is
 * when a menu paints, while the game is paused — and cached forever after.
 */

import * as THREE from 'three';
import { buildWeaponModel } from '../weapons/models/index.js';

export const TUNING = {
  /** Rendered size. 256 is comfortably above the largest place one is displayed. */
  size: 320,
  /** Camera framing: how much larger than the model's bounding sphere to pull back. */
  fit: 1.18,
  /** Three-quarter view. A weapon photographed side-on reads as a stripe. */
  yaw: -0.62,
  pitch: 0.28,
};

const _box = new THREE.Box3();
const _sphere = new THREE.Sphere();
const _center = new THREE.Vector3();

export class IconForge {
  constructor(ctx) {
    this.ctx = ctx;
    this.cache = new Map();
    this.rendered = 0;
    this.failed = 0;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(28, 1, 0.01, 100);

    // Its own lights. The world's sun is placed for a 104 m map at a 13-degree
    // elevation, which on a 30 cm object is a rake so shallow that half of it is
    // black. This is a product shot, so it gets product-shot lighting.
    const key = new THREE.DirectionalLight(0xfff2e2, 4.6);
    key.position.set(-0.8, 1.0, 0.9);
    const fill = new THREE.DirectionalLight(0x9fc4e8, 1.9);
    fill.position.set(1.0, -0.15, 0.55);
    const rim = new THREE.DirectionalLight(0xffd9b0, 2.8);
    rim.position.set(0.55, 0.35, -1.0);
    this.scene.add(key, fill, rim, new THREE.AmbientLight(0xffffff, 0.72));

    this.holder = new THREE.Group();
    this.scene.add(this.holder);

    const s = TUNING.size;
    this.target = new THREE.WebGLRenderTarget(s, s, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      // No depth buffer would be cheaper and would also render the gun inside out.
      depthBuffer: true,
      stencilBuffer: false,
    });

    this.canvas = document.createElement('canvas');
    this.canvas.width = s;
    this.canvas.height = s;
    this.c2d = this.canvas.getContext('2d');
    this.pixels = new Uint8Array(s * s * 4);
  }

  /**
   * A data URL for one weapon, built on first ask and cached.
   *
   * Returns null rather than throwing when the model cannot be built: an icon is
   * decoration, and a menu that fails to open because a thumbnail failed would be a
   * far worse bug than a menu with no thumbnail.
   */
  weapon(id) {
    if (this.cache.has(id)) return this.cache.get(id);
    let url = null;
    try {
      // A fresh model from the pure builder, never the live one out of `ViewModel`:
      // that one is parented into the view scene and held by the renderer between
      // frames, and reparenting it here would take the gun out of the player's hands
      // for as long as the icon takes to draw.
      const m = buildWeaponModel(id, this.ctx);
      url = this._render(m.root);
      this._dispose(m.root);
    } catch (err) {
      this.failed++;
      console.warn('[icons] could not render', id, err);
    }
    this.cache.set(id, url);
    return url;
  }

  /** A data URL for any object you hand it, cached under `key`. */
  object(key, factory) {
    if (this.cache.has(key)) return this.cache.get(key);
    let url = null;
    try {
      const obj = factory();
      if (obj) {
        url = this._render(obj);
        this._dispose(obj);
      }
    } catch (err) {
      this.failed++;
      console.warn('[icons] could not render', key, err);
    }
    this.cache.set(key, url);
    return url;
  }

  /**
   * Frame `obj`, render it, and read it back as a PNG data URL.
   *
   * Framing is from the bounding sphere rather than the box, so a long weapon and a
   * stubby one are both filled to the same visual weight instead of one of them
   * being a distant speck.
   */
  _render(obj) {
    const r = this.ctx.renderer;
    if (!r) return null;

    this.holder.add(obj);
    obj.updateMatrixWorld(true);
    _box.setFromObject(obj);
    if (!isFinite(_box.min.x)) {
      this.holder.remove(obj);
      return null;
    }
    _box.getBoundingSphere(_sphere);
    _box.getCenter(_center);

    const dist = (_sphere.radius * TUNING.fit) / Math.sin((this.camera.fov * Math.PI) / 360);
    const cy = Math.cos(TUNING.pitch);
    this.camera.position.set(
      _center.x + Math.sin(TUNING.yaw) * cy * dist,
      _center.y + Math.sin(TUNING.pitch) * dist,
      _center.z + Math.cos(TUNING.yaw) * cy * dist
    );
    this.camera.lookAt(_center);
    this.camera.updateProjectionMatrix();

    // Save everything we are about to change. Restoring the render target alone is
    // not enough — a left-behind clear colour paints the sky, and a left-behind tone
    // mapping exposure changes the whole game's look until the next reload.
    const prevTarget = r.getRenderTarget();
    const prevClear = r.getClearColor(new THREE.Color());
    const prevAlpha = r.getClearAlpha();
    const prevTone = r.toneMapping;
    const prevExposure = r.toneMappingExposure;

    r.setRenderTarget(this.target);
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.75;
    r.clear(true, true, false);
    r.render(this.scene, this.camera);
    r.readRenderTargetPixels(this.target, 0, 0, TUNING.size, TUNING.size, this.pixels);

    r.setRenderTarget(prevTarget);
    r.setClearColor(prevClear, prevAlpha);
    r.toneMapping = prevTone;
    r.toneMappingExposure = prevExposure;

    this.holder.remove(obj);

    const s = TUNING.size;
    const img = this.c2d.createImageData(s, s);
    // WebGL reads bottom-up; the canvas is top-down. Flipping here rather than with
    // a CSS transform keeps the data URL correct wherever it is used.
    for (let y = 0; y < s; y++) {
      const src = (s - 1 - y) * s * 4;
      img.data.set(this.pixels.subarray(src, src + s * 4), y * s * 4);
    }
    this.c2d.clearRect(0, 0, s, s);
    this.c2d.putImageData(img, 0, 0);
    this.rendered++;
    return this._crop(img, s);
  }

  /**
   * Trim the transparent margin.
   *
   * Framing from the bounding *sphere* is safe — nothing can ever be clipped — but
   * for a weapon it is wasteful: a rifle is framed by its diagonal, so the gun ends
   * up occupying about a third of the height and reads as a smudge in a 34 px row.
   * Rather than try to fit a rotated box analytically, render generously and then
   * cut away whatever turned out to be empty. The result fills its element whatever
   * shape the object was.
   */
  _crop(img, s) {
    const d = img.data;
    let minX = s;
    let minY = s;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        // 8/255 rather than 0: the edges carry a faint antialiased fringe, and
        // cropping to literally-any-alpha keeps a border of almost-nothing.
        if (d[(y * s + x) * 4 + 3] > 8) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null; // nothing was drawn

    const pad = 2;
    const x0 = Math.max(0, minX - pad);
    const y0 = Math.max(0, minY - pad);
    const w = Math.min(s, maxX + pad + 1) - x0;
    const h = Math.min(s, maxY + pad + 1) - y0;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    out.getContext('2d').drawImage(this.canvas, x0, y0, w, h, 0, 0, w, h);
    return out.toDataURL('image/png');
  }

  _dispose(obj) {
    obj.traverse?.((o) => {
      if (!o.isMesh) return;
      o.geometry?.dispose?.();
      // Materials are shared across weapons by the model layer's own cache, so
      // disposing them here would blank every gun built afterwards.
    });
  }

  report() {
    return { cached: this.cache.size, rendered: this.rendered, failed: this.failed };
  }

  dispose() {
    this.target.dispose();
    this.cache.clear();
  }
}
